import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, Truck, Wrench, TrendingUp } from "lucide-react";
import { STEPS } from "@/data/site";
import { EASE, SectionHead } from "@/components/motion/Reveal";

const ICONS = [Search, Truck, Wrench, TrendingUp];

export const HowItWorks = ({ index = "03" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 60%"] });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} className="relative py-28 lg:py-40" data-testid="how-it-works-section">
      <div className="container-x">
        <SectionHead index={index} eyebrow="How It Works" title={["From first call", "to a full floor."]}>
          A simple, hands-on process. You focus on your customers — we handle the equipment.
        </SectionHead>
        <div className="relative mt-20">
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-white/10 lg:block" />
          <motion.div style={{ scaleX }} className="absolute left-0 right-0 top-7 hidden h-px origin-left bg-gradient-to-r from-volt via-volt to-signal lg:block" />
          <ol className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {STEPS.map((s, i) => {
              const Icon = ICONS[i];
              return (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}
                  className="group relative"
                  data-testid={`process-step-${i + 1}`}
                >
                  <div className="relative z-10 grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-ink-800 transition-[border-color,box-shadow] duration-500 group-hover:border-volt/60 group-hover:shadow-[0_0_40px_-6px_rgba(0,240,255,0.5)]">
                    <Icon className="h-5 w-5 text-volt" />
                  </div>
                  <p className="mt-8 font-display text-7xl font-black leading-none tracking-tighter text-outline">{s.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
